var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// Prevent Prism from auto-highlighting
window.Prism = window.Prism || {};
if (typeof Prism.manual === 'undefined')
    Prism.manual = true;
import React from 'jsx-dom';
import Prism from 'prismjs';
import { CodeJar } from 'codejar';
import { debounce } from '@exadel/esl/modules/esl-utils/async/debounce';
import { attr, boolAttr, decorate, listen, memoize } from '@exadel/esl/modules/esl-utils/decorators';
import { UIPPluginPanel } from '../../core/panel/plugin-panel';
import { CopyIcon } from '../copy/copy-button.icon';
import { ResetIcon } from '../reset/reset-button.icon';
import { EditorIcon } from './editor.icon';
/**
 * Editor {@link UIPPlugin} custom element definition
 * Uses Codejar code editor to provide an ability to modify UIP state markup
 */
export class UIPEditor extends UIPPluginPanel {
    get $icon() {
        return React.createElement(EditorIcon, null);
    }
    get $toolbar() {
        var _a;
        const type = this.constructor;
        return (React.createElement("div", { className: type.is + '-toolbar uip-plugin-header-toolbar' },
            this.showCopy ? React.createElement("uip-copy", { class: type.is + '-header-copy', source: this.source },
                React.createElement(CopyIcon, null)) : '',
            ((_a = this.$root) === null || _a === void 0 ? void 0 : _a.storeKey) ? React.createElement("uip-reset", { class: type.is + '-header-reset', source: this.source },
                React.createElement(ResetIcon, null)) : ''));
    }
    /** {@link UIPEditor} section wrapper */
    get $inner() {
        const type = this.constructor;
        return (React.createElement("div", { className: `${type.is}-inner uip-plugin-inner uip-plugin-inner-bg` },
            React.createElement("esl-scrollbar", { class: type.is + '-scrollbar', target: "::next" }),
            React.createElement("div", { className: `uip-plugin-content ${type.is}-container esl-scrollable-content` }, this.$code)));
    }
    /** Code block */
    get $code() {
        const type = this.constructor;
        const lang = this.source === 'js' ? 'javascript' : this.source;
        return (React.createElement("pre", { class: type.is + '-code language-' + lang },
            React.createElement("code", null)));
    }
    /** Wrapped [CodeJar](https://medv.io/codejar/) editor instance */
    get editor() {
        return CodeJar(this.$code, UIPEditor.highlight, { tab: '\t' });
    }
    /** @returns if editing is allowed */
    get editable() {
        return !this.$$cls('readonly');
    }
    /** Changes editor readonly mode */
    set editable(value) {
        this.$$cls('readonly', !value);
        if (value) {
            this.$code.contentEditable = 'plaintext-only';
            // FF doesn't support 'plaintext-only' mode
            if (this.$code.contentEditable !== 'plaintext-only')
                this.$code.contentEditable = 'true';
        }
        else {
            this.$code.contentEditable = 'false';
        }
    }
    /** @returns if the editor is in js readonly mode */
    get isSnippetEditable() {
        var _a, _b;
        return this.source !== 'js' || !((_b = (_a = this.model) === null || _a === void 0 ? void 0 : _a.activeSnippet) === null || _b === void 0 ? void 0 : _b.isJsReadonly);
    }
    /** @returns editor's content */
    get value() {
        return this.editor.toString();
    }
    /** Preformat and set editor's content */
    set value(value) {
        this.editor.updateCode(value);
    }
    connectedCallback() {
        super.connectedCallback();
        this.innerHTML = '';
        // Prefill content
        this.appendChild(this.$header);
        this.appendChild(this.$inner);
        this.appendChild(this.$resize);
        // Initial update
        this._onRootStateChange();
        this._onSnippetChange();
        // Postpone subscription
        Promise.resolve().then(() => { var _a; return (_a = this.editor) === null || _a === void 0 ? void 0 : _a.onUpdate(this._onChange); });
    }
    disconnectedCallback() {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.destroy();
        memoize.clear(this, 'editor');
        super.disconnectedCallback();
    }
    attributeChangedCallback(attrName, oldVal, newVal) {
        super.attributeChangedCallback(attrName, oldVal, newVal);
        this.$header.remove();
        this.$toolbar.remove();
        memoize.clear(this, ['$header', '$toolbar']);
        this.insertAdjacentElement('afterbegin', this.$header);
    }
    /** Callback to call on an editor's content changes */
    _onChange() {
        if (!this.editable)
            return;
        if (this.source === 'js')
            this.model.setJS(this.value, this);
        if (this.source === 'html')
            this.model.setHtml(this.value, this);
    }
    /** Change editor's markup from markup state changes */
    _onRootStateChange(e) {
        if (e && e.isOnlyModifier(this))
            return;
        if ((this.source === 'js' && (!e || e.jsChanges.length)) ||
            (this.source === 'html' && (!e || e.htmlChanges.length)))
            this.value = this.model[this.source];
    }
    /** Handles snippet change to set readonly value */
    _onSnippetChange() {
        this.editable = this.isSnippetEditable;
    }
}
UIPEditor.is = 'uip-editor';
UIPEditor.observedAttributes = ['copy', ...UIPPluginPanel.observedAttributes];
/** Highlight method declaration  */
UIPEditor.highlight = (editor) => Prism.highlightElement(editor, false);
__decorate([
    attr({ defaultValue: 'html' })
], UIPEditor.prototype, "source", void 0);
__decorate([
    boolAttr({ name: 'copy' })
], UIPEditor.prototype, "showCopy", void 0);
__decorate([
    memoize()
], UIPEditor.prototype, "$toolbar", null);
__decorate([
    memoize()
], UIPEditor.prototype, "$inner", null);
__decorate([
    memoize()
], UIPEditor.prototype, "$code", null);
__decorate([
    memoize()
], UIPEditor.prototype, "editor", null);
__decorate([
    decorate(debounce, 2000)
], UIPEditor.prototype, "_onChange", null);
__decorate([
    listen({ event: 'uip:change', target: ($this) => $this.$root })
], UIPEditor.prototype, "_onRootStateChange", null);
__decorate([
    listen({ event: 'uip:snippet:change', target: ($this) => $this.$root })
], UIPEditor.prototype, "_onSnippetChange", null);
